import { createContext, useContext } from 'react';
import type { RowData } from './graph';

export interface DiagramInteraction {
  /** Selected row as `${nodeId}::${rowId}`. */
  selectedRow: string | null;
  /** Node ids to keep at full strength (search matches, changed cards); others are dimmed. */
  matches: Set<string> | null;
  /** Node ids drawn at full strength while the others are drawn softer but readable (the cards a delete reaches). */
  emphasis: Set<string> | null;
  /** Stays the same function, so cards do not draw again when the selection changes elsewhere. */
  selectRow: (nodeId: string, row: RowData) => void;
  /** Cards can be connected to create relationships (sketches). */
  editable: boolean;
  /** A connection is being dragged: every card accepts a drop. */
  connecting: boolean;
  /** Too far out to read the rows: cards draw them as bars (see `zoomedOutAt`). */
  zoomedOut: boolean;
}

export const DiagramContext = createContext<DiagramInteraction>({
  selectedRow: null,
  matches: null,
  emphasis: null,
  selectRow: () => {},
  editable: false,
  connecting: false,
  zoomedOut: false,
});

export const useDiagramInteraction = () => useContext(DiagramContext);

/**
 * Below this zoom (times the device pixel ratio) a row's 12.5px text is under 5 screen pixels,
 * too small to read, so cards draw bars instead: far fewer elements for a large model.
 */
export const ROW_TEXT_ZOOM = 0.4;

export function zoomedOutAt(zoom: number): boolean {
  return zoom * (window.devicePixelRatio || 1) < ROW_TEXT_ZOOM;
}
